/**
 * Web Placements Module - Type Definitions
 *
 * Defines core data structures for web placement system:
 * - WebPlacement: Container for shareable funnel URLs
 * - WebPlacementConfig: Extensible configuration object (mirrors PlacementConfig)
 *
 * Design Principle: config is an extensible object to support future features
 * (themes, analytics, SEO, Stripe) without schema changes.
 *
 * @see /modules/webplacements/MODULE.md for full documentation
 */

import type { ConsentManagementConfig } from '@modules/analytics-settings/types/analytics.types';
import type { PaddleCheckoutPolicy } from '@segmently/schema-models/payments';
import type { WebABPublicationRecord } from '@modules/shared-types';
import { resolvePublicEdgeGatewayBaseUrl } from '@modules/webplacements/utils/publicEdgeUrl';

// =============================================================================
// PLACEMENT TYPE (Single vs A/B Test)
// =============================================================================

/**
 * Web placement type discriminator
 *
 * @description
 * - single: Regular web placement with direct funnel configuration
 * - ab_test: A/B test that routes to existing published WebPlacements
 */
export type WebPlacementType = 'single' | 'ab_test';

// =============================================================================
// STATUS TYPES
// =============================================================================

/**
 * Web placement deployment status
 *
 * @description
 * - draft: Initial status, or after editing
 * - published: After successful deployment to GCS
 */
export type WebPlacementStatus = 'draft' | 'published';

// =============================================================================
// VALIDATION CONSTANTS
// =============================================================================

/**
 * Web placement validation constants
 *
 * @description Alias is used to generate unique URLs: /{alias}
 * Must be URL-safe: letters (including camelCase), numbers, hyphens only
 */
export const WEB_PLACEMENT_VALIDATION = {
  /** URL-safe pattern: letters (including camelCase), numbers, hyphens, underscores */
  ALIAS_PATTERN: /^[a-zA-Z0-9_-]+$/,
  /** Minimum alias length */
  ALIAS_MIN_LENGTH: 3,
  /** Maximum alias length */
  ALIAS_MAX_LENGTH: 50,
  /** Reserved aliases that cannot be used */
  RESERVED_ALIASES: ['api', 'admin', 'health', 'status', 'config'] as const,
  /** Minimum name length */
  NAME_MIN_LENGTH: 1,
  /** Maximum name length */
  NAME_MAX_LENGTH: 100,
  /** Maximum description length */
  DESCRIPTION_MAX_LENGTH: 500,
} as const;

/**
 * Check if a web alias is valid (format only, not uniqueness)
 *
 * @param alias - Web alias to validate
 * @returns true if alias format is valid
 */
export const isValidWebAlias = (alias: string): boolean => {
  if (!alias) return false;
  if (alias.length < WEB_PLACEMENT_VALIDATION.ALIAS_MIN_LENGTH) return false;
  if (alias.length > WEB_PLACEMENT_VALIDATION.ALIAS_MAX_LENGTH) return false;
  if (!WEB_PLACEMENT_VALIDATION.ALIAS_PATTERN.test(alias)) return false;
  if ((WEB_PLACEMENT_VALIDATION.RESERVED_ALIASES as readonly string[]).includes(alias)) return false;
  return true;
};

/**
 * Check if a web alias is reserved
 *
 * @param alias - Web alias to check
 * @returns true if alias is reserved and cannot be used
 */
export const isReservedWebAlias = (alias: string): boolean =>
  (WEB_PLACEMENT_VALIDATION.RESERVED_ALIASES as readonly string[]).includes(alias);

// =============================================================================
// A/B TEST VALIDATION CONSTANTS
// =============================================================================

/**
 * Web A/B test validation constants
 *
 * @description Constraints for A/B test variants (same as iOS placements)
 * @see VARIANT_VALIDATION in placement.types.ts
 */
export const WEB_AB_VALIDATION = {
  /** Minimum number of variants in A/B test (1 = single-variant allowed for gradual rollout) */
  VARIANT_MIN_COUNT: 1,
  /** Maximum number of variants in A/B test */
  VARIANT_MAX_COUNT: 10,
  /** Required sum of all variant weights */
  WEIGHT_SUM: 100,
  /** Minimum weight per variant (0 = disabled) */
  WEIGHT_MIN: 0,
  /** Maximum weight per variant */
  WEIGHT_MAX: 100,
} as const;

export const WEB_AB_VARIANT_ROLES = ['baseline', 'control', 'treatment', 'holdout'] as const;

export type WebABVariantRole = typeof WEB_AB_VARIANT_ROLES[number];

export type WebABIterationStatus = 'published' | 'superseded' | 'archived';

export type WebABPublishMode = 'update_current' | 'new_iteration';

/** Whether an allocation epoch is randomized or only serves one effective variant. */
export type WebABAllocationMode = 'delivery_only' | 'experiment';

/** Publication action selected automatically from the routing hash. */
export type WebABEpochAction = 'reused' | 'created';

export const getDefaultWebABVariantRole = (index: number): WebABVariantRole =>
  index === 0 ? 'baseline' : 'treatment';

export const isWebABVariantRole = (value: unknown): value is WebABVariantRole =>
  typeof value === 'string' && (WEB_AB_VARIANT_ROLES as readonly string[]).includes(value);

// =============================================================================
// A/B TEST VARIANT TYPE
// =============================================================================

/**
 * Web A/B test variant - reference to an existing published WebPlacement
 *
 * @description
 * Unlike iOS variants which embed config directly, Web A/B variants
 * reference existing published WebPlacements. This enables:
 * - Reuse of already-deployed funnels
 * - Independent testing of each variant before A/B test
 * - Variant funnels remain directly accessible via their own alias
 *
 * Denormalized fields (alias, name) are stored for:
 * - Fast Edge Gateway resolution (no extra Firestore reads)
 * - UI display without loading referenced placements
 */
export interface WebABVariant {
  /** Variant ID (e.g., 'A', 'B', 'control') */
  id: string;
  /** Human-readable variant name */
  name: string;
  /** Human-readable description of what this variant changes and measures */
  description?: string;
  /** Experiment role for analysis and reporting */
  role?: WebABVariantRole;
  /** Optional operator tags for grouping variants in admin/report lookup */
  tags?: string[];
  /** Traffic weight (0-100, sum of all variants = 100) */
  weight: number;
  /** Reference to published WebPlacement ID */
  webPlacementId: string;
  /** Denormalized: alias of referenced WebPlacement (for GCS path resolution) */
  webPlacementAlias: string;
  /** Denormalized: name of referenced WebPlacement (for UI display) */
  webPlacementName: string;
}

export interface WebABIterationVariantSnapshot {
  /** Variant ID inside the A/B test */
  id: string;
  /** Human-readable variant name at publish time */
  name: string;
  /** Human-readable description at publish time */
  description?: string;
  /** Experiment role at publish time */
  role: WebABVariantRole;
  /** Optional variant tags at publish time */
  tags?: string[];
  /** Traffic weight at publish time */
  weight: number;
  /** Referenced Web Placement ID at publish time */
  webPlacementId: string;
  /** Referenced Web Placement alias at publish time */
  webPlacementAlias: string;
  /** Referenced Web Placement name at publish time */
  webPlacementName: string;
}

export interface WebABIteration {
  /** Immutable iteration ID */
  id: string;
  /** Monotonic per-test sequence, starting at 1 */
  sequence: number;
  /** Human-readable iteration name */
  name: string;
  /** Human-readable iteration description */
  description?: string;
  /** Optional iteration tags */
  tags?: string[];
  /** Optional reason this iteration was published */
  changeReason?: string;
  /** Iteration lifecycle status */
  status: WebABIterationStatus;
  /** ISO timestamp when the iteration was published */
  publishedAt: string;
  /** User email who published the iteration */
  publishedBy: string;
  /** ISO timestamp when this iteration was superseded */
  supersededAt?: string;
  /** Salt used for deterministic routing */
  abSalt: string;
  /** Deterministic hash of routing-critical state */
  routingHash: string;
  /** Derived from the number of variants with positive traffic weight */
  allocationMode?: WebABAllocationMode;
  /** Variant routing snapshot at publish time */
  variants: WebABIterationVariantSnapshot[];
}

export interface WebABIterationPublishInput {
  /** Publish mode: republish current config or create a new immutable iteration */
  mode: WebABPublishMode;
  /** Name for new iteration */
  name?: string;
  /** Description for new iteration */
  description?: string;
  /** Optional iteration tags */
  tags?: string[];
  /** Optional reason this iteration was created */
  changeReason?: string;
}

// =============================================================================
// UNIFIED PARAMETER SYSTEM
// =============================================================================

/**
 * Parameter source types for unified parameter system
 *
 * @description
 * - url: URL query parameters (utm_source, gclid, etc.)
 * - server: Server-injected data (geoData.country, ip, etc.)
 * - custom: Custom variables set via analytics scripts (lead_score)
 * - variable: V2 variable-based inputs (variableId)
 */
export type ParameterSource = 'url' | 'server' | 'custom' | 'variable';

/**
 * Source details for each parameter type
 */
export interface ParameterSourceDetails {
  /** For url source: original URL parameter name (e.g., 'utm_source') */
  urlParam?: string;
  /** For server source: path in SERVER_DATA (e.g., 'geoData.country') */
  serverPath?: string;
  /** For variable source: variable ID from FlowDocument.variables */
  variableId?: string;
  /** For variable source: variable type for proper value extraction */
  variableType?: 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'enum[]';
}

/**
 * Unified parameter definition
 *
 * @description
 * All parameters (incoming URL params, server data, variable inputs, custom variables)
 * are defined using this unified structure. Defined parameters are available:
 * - In redirect URL as {{key}} placeholders
 * - In webhook request body
 * - In Stripe metadata
 * - In conditional redirect rules
 */
export interface ParameterDefinition {
  /** Unique key used as {{key}} in redirect URL template */
  key: string;
  /** Parameter source type */
  source: ParameterSource;
  /** Source-specific details */
  sourceDetails: ParameterSourceDetails;
  /** Human-readable label for UI */
  label: string;
  /** Data type for validation and display */
  dataType: 'string' | 'number' | 'boolean';
  /** Optional description for tooltips */
  description?: string;
}

/**
 * Preset server data sources available by default
 *
 * suggestedKey: The internal key that will be used in Stripe metadata.
 * ServerAnalyticsForwarder expects specific key names for Facebook CAPI attribution:
 * - client_ip (not just 'ip')
 * - client_user_agent (not just 'userAgent')
 * - facebook.fbp (with dot, not 'fbp')
 * - facebook.fbc (with dot, not 'fbc')
 *
 * @see ServerAnalyticsForwarder.enrichUserData() for expected metadata keys
 */
export const PRESET_SERVER_SOURCES = [
  { path: 'geoData.country', label: 'Country', dataType: 'string' as const },
  { path: 'geoData.region', label: 'Region', dataType: 'string' as const },
  { path: 'geoData.city', label: 'City', dataType: 'string' as const },
  // IP and User Agent - special keys for Facebook CAPI attribution
  { path: 'ip', label: 'IP Address', dataType: 'string' as const, suggestedKey: 'client_ip' },
  { path: 'userAgent', label: 'User Agent', dataType: 'string' as const, suggestedKey: 'client_user_agent' },
  { path: 'platformUserId', label: 'Platform User ID', dataType: 'string' as const, suggestedKey: 'platform_user_id' },
  // Facebook tracking cookies for CAPI attribution - keys must include dot!
  // @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
  { path: 'facebook.fbp', label: 'Facebook Browser ID (fbp)', dataType: 'string' as const, suggestedKey: 'facebook.fbp' },
  { path: 'facebook.fbc', label: 'Facebook Click ID (fbc)', dataType: 'string' as const, suggestedKey: 'facebook.fbc' },
  // TikTok tracking cookies for Events API attribution - keys must include dot!
  // @see https://ads.tiktok.com/help/article/events-api
  { path: 'tiktok.ttp', label: 'TikTok Browser ID (ttp)', dataType: 'string' as const, suggestedKey: 'tiktok.ttp' },
  { path: 'tiktok.ttclid', label: 'TikTok Click ID (ttclid)', dataType: 'string' as const, suggestedKey: 'tiktok.ttclid' },
] as const;

/**
 * Common URL parameters for quick setup
 */
export const COMMON_URL_PARAMS = [
  { param: 'utm_source', label: 'UTM Source' },
  { param: 'utm_medium', label: 'UTM Medium' },
  { param: 'utm_campaign', label: 'UTM Campaign' },
  { param: 'utm_content', label: 'UTM Content' },
  { param: 'utm_term', label: 'UTM Term' },
  { param: 'gclid', label: 'Google Click ID' },
  { param: 'fbclid', label: 'Facebook Click ID' },
  { param: 'ttclid', label: 'TikTok Click ID' },
  { param: 'ref', label: 'Referral' },
] as const;

// =============================================================================
// DESTINATION MAPPING TYPES
// =============================================================================

/**
 * Mapping configuration for destination parameters
 *
 * @description
 * Used in the 2-field pattern for destination mapping:
 * 1. Source Type (dropdown): URL | Server | Variable | Custom
 * 2. Source Item (dropdown from selected source type)
 *
 * The output name is automatically the parameter's internal key as defined
 * in source sections. Users define keys in sources, destinations just reference them.
 */
export interface DestinationParameterMapping {
  /** Source type: url | server | variable | custom */
  sourceType: ParameterSource;
  /** Key of the parameter from that source (also used as output name) */
  sourceKey: string;
}

// =============================================================================
// VARIABLE BINDING TYPES (V2 only)
// =============================================================================

/**
 * Variable binding: maps an external source (URL param or server data) to a V2 variable.
 * Applied at runtime BEFORE the first screen renders, so conditions depending on
 * these variables evaluate correctly from the start.
 */
export interface VariableBinding {
  /** Source type: URL parameter or server-injected data */
  sourceType: 'url' | 'server';
  /** For 'url': URL query parameter name; for 'server': dot-path in SERVER_DATA */
  sourceKey: string;
  /** Target V2 variable ID (from FlowDocument.variables) */
  variableId: string;
  /** Display-only: variable name for UI rendering */
  variableName?: string;
  /** Variable type for proper parsing at runtime (string, number, boolean, enum, etc.) */
  variableType?: string;
}

// =============================================================================
// ENTRY SCREEN RULES
// =============================================================================

/**
 * Operators for entry screen rules.
 * Starting with 'eq' only in UI, extensible for future compound conditions.
 */
export type EntryScreenOperator = 'eq' | 'neq' | 'in' | 'notIn' | 'contains' | 'regex';

/**
 * Entry screen rule — selects a starting screen based on URL parameter values.
 * Rules are evaluated top-to-bottom; first match wins.
 * If no rules match, fallback to FlowDocument.launchScreenId.
 *
 * @example
 * // campaign_id=summer → start on "Summer Quiz" screen
 * { id: 'uuid', paramKey: 'campaign_id', operator: 'eq', value: 'summer',
 *   targetScreenId: 'screen_abc', targetScreenName: 'Summer Quiz', isActive: true }
 */
export interface EntryScreenRule {
  /** Unique rule identifier (UUID) for UI key */
  id: string;
  /** URL parameter key (must exist in WebPlacementConfig.urlParams[].key) */
  paramKey: string;
  /** Comparison operator (default: 'eq') */
  operator: EntryScreenOperator;
  /** Expected value(s) to compare against */
  value: string | string[];
  /** Target screen ID to start from when rule matches */
  targetScreenId: string;
  /** Display-only: screen name for UI rendering */
  targetScreenName?: string;
  /** Toggle to disable without deletion */
  isActive: boolean;
}

// =============================================================================
// CONFIGURATION TYPES
// =============================================================================

/**
 * WebPlacementConfig - Extensible configuration object
 *
 * @description
 * Contains funnel reference + redirect URL (most important!) + parameters + settings.
 * All sections are enabled by default (no enable toggles needed).
 *
 * @see PlacementConfig in firebase-core for iOS equivalent
 */
export interface WebPlacementConfig {
  /** Provider surface/navigation; runtime frame targets are never persisted. */
  paddleCheckout?: PaddleCheckoutPolicy;
  /** WebPlacement Firestore document ID (for analytics: webOnboardingId) */
  id?: string;
  /** Onboarding (funnel) ID - required */
  funnelId: string;
  /** Version ID within the onboarding - required */
  versionId: string;
  /** Human-readable entity name for display (e.g., "Summer Promo v1.2") - used for webOnboardingName */
  entityName?: string;
  /** Onboarding/Funnel name for analytics */
  funnelName?: string;
  /** Version name for analytics */
  versionName?: string;

  // === REDIRECT URL (Most important - displayed prominently) ===
  /**
   * Redirect URL template with {{placeholder}} syntax
   * @example "https://example.com/thank-you?email={{email}}&source={{utm_source}}"
   */
  redirectUrl?: string;
  /** Fallback URL when template parsing fails */
  fallbackUrl?: string;
  /** Whether to URL-encode parameter values (default: true) */
  urlEncode?: boolean;

  // === UNIFIED PARAMETERS (Legacy) ===
  /**
   * All parameter definitions combined (legacy format)
   * @deprecated Use source-specific arrays (urlParams, serverData, etc.) instead
   */
  parameters?: ParameterDefinition[];

  // === SOURCE PARAMETERS (DEFINE) - New Format ===
  /** URL query parameters (utm_source, gclid, etc.) */
  urlParams?: ParameterDefinition[];
  /** Server-injected data (geoData.country, ip, etc.) */
  serverData?: ParameterDefinition[];
  /** V2 variable-based inputs */
  variableInputs?: ParameterDefinition[];
  /** Custom variables set via analytics scripts */
  customVariables?: ParameterDefinition[];

  // === DESTINATION MAPPINGS (USE) ===
  /** Parameters to add to redirect URL */
  redirectMappings?: DestinationParameterMapping[];
  /** Parameters to add to Stripe PaymentIntent metadata */
  stripeMappings?: DestinationParameterMapping[];

  // === VARIABLE BINDINGS (V2 only) ===
  /**
   * Bindings from external sources (URL params, server data) to V2 variables.
   * Applied at runtime BEFORE the first screen renders, so conditions depending
   * on these variables evaluate correctly from the start.
   * Only used when funnel schemaVersion is 2.x
   */
  variableBindings?: VariableBinding[];

  // === ENTRY SCREEN RULES (V2 only) ===
  /**
   * Rules that override launchScreenId based on URL parameter values.
   * Evaluated in array order; first matching rule wins.
   * Only used when funnel schemaVersion is 2.x.
   * State persistence takes priority: if user has saved progress, rules are skipped.
   */
  entryScreenRules?: EntryScreenRule[];

  // === CONDITIONAL REDIRECT RULES ===
  /** Conditional redirect rules (evaluated when no simple redirectUrl matches) */
  conditionalRedirect?: WebConditionalRedirectConfig;

  // === DATA COLLECTION ===
  /** Data collection configuration (webhooks, Stripe metadata) */
  dataCollection?: WebDataCollectionConfig;

  // === CUSTOM ANALYTICS ===
  /** Custom analytics script configuration (gtag, fbq) */
  customAnalytics?: WebCustomAnalyticsConfig;

  // === SEO ===
  /** SEO metadata for social sharing */
  seo?: WebSEOConfig;

  // === STRIPE ===
  /** Stripe email variable configuration */
  stripe?: WebStripeConfig;

  // === CONSENT MANAGEMENT (GDPR) ===
  /**
   * Funnel-level GDPR consent configuration
   *
   * @description
   * - If present: Uses funnel-specific consent settings
   * - If absent: No GDPR consent management for this funnel
   * - No fallback to project-level settings (complete independence)
   * - Allows per-funnel customization for different audiences/regions
   *
   * @example
   * // EU-targeted funnel with strict consent
   * consentManagement: {
   *   enabled: true,
   *   defaultConsent: 'denied',
   *   bannerText: 'We need your consent for analytics...',
   *   privacyPolicyUrl: 'https://example.com/privacy'
   * }
   *
   * // US-targeted funnel without consent banner
   * consentManagement: undefined  // No GDPR for this funnel
   *
   * @see ConsentManagementConfig from analytics-settings
   */
  consentManagement?: ConsentManagementConfig;

  // === USER DATA PERSISTENCE (V2 only) ===
  /**
   * Whether to persist user progress (current screen, inputs, history) to localStorage.
   * When false, funnel always starts fresh on each visit.
   * Only affects V2 funnels (FunnelEngineV2).
   *
   * @default true (backward compatible — existing placements persist by default)
   */
  saveUserData?: boolean;

  // === PAYMENT METHOD PERSISTENCE (V2 only, Upsell) ===
  /**
   * Whether to remember the payment method for frictionless upsell flows.
   * When true, after a successful first payment the customer's payment method
   * is stored in-memory for the current session, enabling automatic re-billing
   * on subsequent paywall screens (no card form shown).
   * When false, every paywall screen shows the full payment form.
   * Only affects V2 funnels (FunnelEngineV2).
   *
   * @default true (backward compatible — existing placements use upsell by default)
   */
  rememberPaymentMethod?: boolean;
}

// =============================================================================
// DATA COLLECTION TYPES
// =============================================================================

/**
 * Data collection config for WebPlacement
 *
 * @description
 * No enable toggles - all sections are enabled by default.
 * Empty arrays/objects mean nothing is configured (but section is still "enabled").
 */
export interface WebDataCollectionConfig {
  /** Stripe metadata configuration */
  stripeMetadata?: WebStripeMetadataConfig;
  /** Webhooks to call on funnel events */
  webhooks?: WebhookConfig[];
}

/**
 * Stripe metadata configuration
 */
export interface WebStripeMetadataConfig {
  /** Maximum keys to include (Stripe limit: 50, default: 20) */
  maxKeys?: number;
  /** Priority fields when approaching limit (keys from parameters) */
  priorityFields?: string[];
}

/**
 * Webhook trigger event types
 *
 * @description
 * - screen_show: Fire-and-forget when screen appears (ScreenDidAppear)
 * - screen_exit: Fire-and-forget when leaving screen (ScreenDisappeared)
 * - screen_enter_await: Wait for response on screen enter, save to custom variables
 * - onboarding_finish: Fire-and-forget when funnel completes (OnboardingFinished)
 */
export type WebhookTriggerEvent =
  | 'screen_show'
  | 'screen_exit'
  | 'screen_enter_await'
  | 'onboarding_finish';

/**
 * Response mapping for screen_enter_await webhook
 *
 * @description
 * Maps webhook response JSON paths to custom variable keys.
 * The custom variables are then available for:
 * - Conditional edges (navigation rules)
 * - Redirect URL placeholders ({{customVarKey}})
 * - Other webhooks
 */
export interface WebhookResponseMapping {
  /** Path in JSON response (e.g., "data.score", "result.tier") */
  responsePath: string;
  /** Key of custom variable to store the value */
  customVariableKey: string;
  /** Data type for parsing */
  dataType: 'string' | 'number' | 'boolean';
}

/**
 * Webhook configuration
 *
 * @description
 * Webhooks are triggered on funnel events. No enable toggle needed -
 * if a webhook is in the list, it's active.
 *
 * Trigger types:
 * - screen_show, screen_exit, onboarding_finish: fire-and-forget
 * - screen_enter_await: waits for response, maps data to custom variables
 */
export interface WebhookConfig {
  /** Unique identifier */
  id: string;
  /** Human-readable name */
  name: string;
  /** Request URL */
  url: string;
  /** HTTP method */
  method: 'GET' | 'POST';
  /** When to trigger this webhook */
  triggerEvent: WebhookTriggerEvent;
  /** For screen-based triggers: specific screen IDs (empty = all screens) */
  triggerScreenIds?: string[];
  /** Bearer token for Authorization header */
  bearerToken?: string;
  /** Custom headers */
  headers?: Record<string, string>;
  /** Send all defined parameters in request body */
  sendAllParameters?: boolean;
  /** Specific parameter keys to send (if not sendAllParameters) */
  parameterKeys?: string[];

  /**
   * Response mapping for screen_enter_await trigger
   *
   * @description
   * Maps response JSON paths to custom variable keys.
   * Only used when triggerEvent is 'screen_enter_await'.
   *
   * @example
   * [
   *   { responsePath: "score", customVariableKey: "leadScore", dataType: "number" },
   *   { responsePath: "data.tier", customVariableKey: "userTier", dataType: "string" }
   * ]
   */
  responseMapping?: WebhookResponseMapping[];
}

// =============================================================================
// CUSTOM ANALYTICS TYPES
// =============================================================================

/**
 * Custom Analytics configuration
 *
 * @description
 * No enable toggle - if script is provided, it's executed.
 * Custom parameters defined here can set values via window.segmentlySDK.setCustomData()
 * that are then available in redirect URL and webhooks.
 *
 * NOTE: Custom parameters are now defined as ParameterDefinition with source='custom'
 * in the main parameters array. This config only holds the script itself.
 */
export interface WebCustomAnalyticsConfig {
  /** User's JavaScript code (gtag, fbq, etc.) */
  script?: string;
  /** Where to inject script: head or body (default: head) */
  placement?: 'head' | 'body';
}

// =============================================================================
// SEO TYPES
// =============================================================================

/**
 * SEO configuration for web placements
 */
export interface WebSEOConfig {
  /** Page title (<title> tag) */
  title?: string;
  /** Meta description */
  description?: string;
  /** Open Graph image URL */
  ogImage?: string;
}

// =============================================================================
// CONDITIONAL REDIRECT TYPES
// =============================================================================

/**
 * Conditional redirect rule - evaluated in priority order
 *
 * @description
 * Rules are evaluated when conditional redirects are configured.
 * First matching rule wins, otherwise fallback to main redirectUrl.
 */
export interface WebRedirectRule {
  /** Unique rule identifier */
  id: string;
  /** Human-readable rule name */
  name: string;
  /** Evaluation priority (0-1000, higher wins) */
  priority: number;
  /** Toggle to disable without deletion */
  isActive: boolean;
  /** Conditions that must ALL match (AND logic) */
  conditions: RedirectCondition[];
  /** Target URL when rule matches (can use {{placeholders}}) */
  targetUrl: string;
}

/**
 * Condition for redirect rule
 *
 * @description
 * Field references a parameter key from the parameters array.
 * Example: if you defined a parameter with key='utm_source',
 * the field here would be 'utm_source' (not 'url.utm_source').
 */
export interface RedirectCondition {
  /** Parameter key from defined parameters */
  field: string;
  /** Comparison operator */
  operator: RedirectOperator;
  /** Value(s) to compare against */
  value: string | string[];
}

export type RedirectOperator =
  | 'eq'       // equals
  | 'neq'      // not equals
  | 'in'       // value in list
  | 'notIn'    // value not in list
  | 'contains' // string contains
  | 'regex';   // regex match

/**
 * Conditional redirect configuration
 *
 * @description
 * Provides rule-based redirects when you need different URLs based on conditions.
 * If no rules match, falls back to the main redirectUrl in WebPlacementConfig.
 *
 * No enable toggle - if rules array is not empty, conditional redirect is active.
 */
export interface WebConditionalRedirectConfig {
  /** Rules evaluated in priority order (first match wins) */
  rules: WebRedirectRule[];
}

/**
 * @deprecated Use WebConditionalRedirectConfig instead
 * Kept for backward compatibility during migration
 */
export interface WebRedirectConfig {
  /** @deprecated - no longer used */
  enabled?: boolean;
  /** Rules evaluated in priority order (first match wins) */
  rules: WebRedirectRule[];
  /** @deprecated - use WebPlacementConfig.redirectUrl instead */
  fallbackUrl?: string;
  /** @deprecated - use WebPlacementConfig.urlEncode instead */
  urlEncode?: boolean;
}

// =============================================================================
// STRIPE TYPES
// =============================================================================

/**
 * Stripe integration configuration
 */
export interface WebStripeConfig {
  /** Variable ID containing the email value */
  emailVariableId?: string;
}

// =============================================================================
// MAIN ENTITY TYPE
// =============================================================================

/**
 * Web Placement entity stored in Firestore
 *
 * @description Stored in: projects/{projectId}/webPlacements/{id}
 *
 * Supports two types (like iOS placements):
 * - single: Regular web placement with direct funnel configuration
 * - ab_test: A/B test that routes to existing published WebPlacements
 *
 * Key differences from Placement (iOS):
 * - Simpler schema (no targeting rules)
 * - Project-scoped alias uniqueness (unique within project, not globally)
 * - Direct SSG deployment
 * - Web-specific status flow (draft → published)
 */
export interface WebPlacement {
  /** Auto-generated document ID from Firebase */
  id: string;

  /** Human-readable placement name (required, 1-100 chars) */
  name: string;

  /**
   * URL alias for /{alias} (unique within project)
   *
   * @description
   * - 3-50 characters
   * - Letters (including camelCase), numbers, hyphens only
   * - Must be unique within the project (not globally)
   * - Immutable after creation (changing would break published URLs)
   */
  alias: string;

  /** Optional description (0-500 chars) */
  description?: string;

  // === TYPE DISCRIMINATOR ===

  /**
   * Placement type: single (direct funnel) or ab_test (variant routing)
   *
   * @description
   * - single: Uses `config` field for funnel configuration
   * - ab_test: Uses `variants` field for A/B routing to published placements
   *
   * @default 'single' (for backward compatibility)
   */
  type: WebPlacementType;

  // === SINGLE TYPE FIELDS ===

  /**
   * Extensible configuration object (for type: 'single')
   *
   * @description Contains funnel reference + future extensions
   * Required for type: 'single', undefined for type: 'ab_test'
   * @see WebPlacementConfig
   */
  config?: WebPlacementConfig;

  // === A/B TEST TYPE FIELDS ===

  /**
   * A/B test variants (for type: 'ab_test')
   *
   * @description
   * Array of 2-10 variants, each referencing a published WebPlacement.
   * Weights must sum to 100. Variant selection uses MurmurHash3.
   * Required for type: 'ab_test', undefined for type: 'single'
   */
  variants?: WebABVariant[];

  /** Current published A/B iteration snapshot (for type: 'ab_test') */
  abCurrentIteration?: WebABIteration;

  /** Last allocated A/B iteration sequence (for type: 'ab_test') */
  abIterationSequence?: number;

  /** Monotonic publication audit sequence shared by UI and CLI publishers. */
  publishRevision?: number;

  /** Latest publication audit record ID. */
  latestPublicationId?: string;

  /** Denormalized latest publication audit record for list/filter reads. */
  latestPublication?: WebABPublicationRecord;

  /**
   * Salt for deterministic A/B variant selection
   *
   * @description
   * Used in hash formula: murmurhash3(platformUserId + alias + abSalt)
   * Auto-generated on A/B test creation. Can be reset to redistribute users.
   * Only used when type: 'ab_test'
   */
  abSalt?: string;

  /**
   * Folder ID for organizing A/B tests (for type: 'ab_test')
   *
   * @description
   * - null or undefined: A/B test is at root level (not in any folder)
   * - string: A/B test is inside the folder with this ID
   * Only used when type: 'ab_test'. Single placements don't use folders.
   */
  folderId?: string | null;

  /** Optional tags for A/B test organization and report lookup */
  tags?: string[];

  /**
   * Source discriminator (v2.20.0). 'article' marks placements auto-created
   * by the article "Publish with payments (web)" action — they are EXCLUDED
   * from all placement lists and pickers (same isolation as kind:'article'
   * onboarding roots). Absent for regular funnel placements.
   */
  sourceKind?: 'article';

  // === Status & Deployment ===

  /** Deployment status: draft or published */
  status: WebPlacementStatus;

  /** ISO timestamp - when last published (undefined if never published) */
  publishedAt?: string;

  /** User email who last published */
  publishedBy?: string;

  /** Full URL after publish (e.g., https://dev-api.segmently.ai/summer-promo) */
  publishedUrl?: string;

  /** Web-shell version baked into the config at publish time (e.g., "1.0.0") */
  webShellVersion?: string;

  // === Timestamps ===

  /** ISO timestamp - creation time */
  createdAt: string;

  /** ISO timestamp - last update time */
  updatedAt: string;

  /** User email who created this placement */
  createdBy: string;
}

// =============================================================================
// CRUD INPUT TYPES
// =============================================================================

/**
 * Input for creating a new web placement (single type)
 */
export interface CreateWebPlacementInput {
  /** Human-readable name (1-100 chars) */
  name: string;
  /** URL alias (3-50 chars, unique within project) */
  alias: string;
  /** Optional description */
  description?: string;
  /** Placement type (default: 'single') */
  type?: WebPlacementType;
  /** Configuration with funnel reference (required for type: 'single') */
  config?: WebPlacementConfig;
  /** A/B test variants (required for type: 'ab_test') */
  variants?: WebABVariant[];
  /** User email creating the placement */
  createdBy: string;
  /** Optional folder ID for A/B test organization (only used when type: 'ab_test') */
  folderId?: string | null;
  /** Optional tags for A/B test organization and report lookup */
  tags?: string[];
  /** Source discriminator (v2.20.0): 'article' = auto-created for an article, hidden from pickers */
  sourceKind?: 'article';
}

/**
 * Input for creating a new A/B test web placement
 *
 * @description Type-safe input specifically for A/B test creation
 */
export interface CreateWebABTestInput {
  /** Human-readable name (1-100 chars) */
  name: string;
  /** URL alias (3-50 chars, unique within project) */
  alias: string;
  /** Optional description */
  description?: string;
  /** A/B test variants (2-10, weights sum to 100) */
  variants: WebABVariant[];
  /** User email creating the placement */
  createdBy: string;
  /** Optional tags for A/B test organization and report lookup */
  tags?: string[];
}

/**
 * Input for updating an existing web placement
 *
 * @description Note: type is immutable. Alias can only be changed while the placement is draft.
 */
export interface UpdateWebPlacementInput {
  /** Human-readable name (1-100 chars) */
  name?: string;
  /** URL alias (editable only while status is draft) */
  alias?: string;
  /** Optional description */
  description?: string;
  /** Configuration with funnel reference (for type: 'single') */
  config?: WebPlacementConfig;
  /** A/B test variants (for type: 'ab_test') */
  variants?: WebABVariant[];
  /** A/B test salt for deterministic hashing (for type: 'ab_test') */
  abSalt?: string;
  /** Optional tags for A/B test organization and report lookup */
  tags?: string[];
}

// =============================================================================
// DEPLOYMENT TYPES
// =============================================================================

/**
 * A funnel variable without an explicit analytics collection decision,
 * as reported by the Builder Service privacy gate
 * (`analytics_variable_privacy_review_required` evidence).
 */
export interface UnreviewedAnalyticsVariable {
  variableId: string;
  variableName?: string;
}

/**
 * Healthcheck issue attached to a Builder Service publish failure.
 * Mirrors the server shape from `POST /api/publish/web` (422) and
 * `POST /api/publish/web/healthcheck`.
 */
export interface PublishWebHealthcheckIssue {
  id?: string;
  severity?: 'error' | 'warning' | string;
  message?: string;
  path?: string;
  evidence?: {
    unreviewedAnalyticsVariables?: UnreviewedAnalyticsVariable[];
    [key: string]: unknown;
  };
}

/**
 * One unreviewed variable found by the SERVER publish-privacy preflight,
 * annotated with the funnel/version (and A/B variant label) it belongs to so
 * review decisions can be saved back to the owning funnel.
 */
export interface PrivacyPreflightVariable {
  funnelId: string;
  versionId: string;
  /** Variant name for A/B review rows; absent on single placements. */
  variantLabel?: string;
  variableId: string;
  variableName?: string;
}

/**
 * Outcome of the server-driven publish privacy preflight.
 * `unavailable` = the gate could not be evaluated (network, 503 settings) —
 * callers should proceed to publish and let the server gate stay authoritative.
 */
export interface PrivacyPreflightResult {
  status: 'passed' | 'review_required' | 'unavailable';
  unreviewed: PrivacyPreflightVariable[];
}

/**
 * Result of web placement deployment
 */
export interface WebPlacementDeployResult {
  /** Whether deployment was successful */
  success: boolean;
  /** Full URL where funnel is accessible */
  webUrl: string;
  /** GCS file paths created */
  gcsFiles: string[];
  /** Error message if failed */
  error?: string;
  /** Current or newly created A/B iteration ID */
  abIterationId?: string;
  /** Whether this publish reused the current epoch or created a new one */
  abEpochAction?: WebABEpochAction;
  /** Stable machine-readable error code for publication conflicts */
  errorCode?: string;
  /** Healthcheck issues from the Builder Service failure (evidence for review UIs) */
  issues?: PublishWebHealthcheckIssue[];
}

/**
 * Typed deployment error exposing the machine-readable code and healthcheck
 * evidence. Kept as an `Error` subclass so existing `error.message` renderers
 * keep working.
 */
export class WebPlacementDeployError extends Error {
  constructor(
    message: string,
    /** Stable machine-readable error code (e.g. analytics_variable_privacy_review_required) */
    public readonly errorCode?: string,
    /** Healthcheck issues attached to the failure */
    public readonly issues?: PublishWebHealthcheckIssue[],
  ) {
    super(message);
    this.name = 'WebPlacementDeployError';
  }
}

/**
 * Parameters for deploy operation
 */
export interface WebPlacementDeployParams {
  /** Project ID */
  projectId: string;
  /** Web Placement ID to deploy */
  webPlacementId: string;
  /** User performing the deployment */
  userId: string;
  /** Optional A/B publish mode and metadata */
  abIteration?: WebABIterationPublishInput;
}

// =============================================================================
// URL UTILITIES
// =============================================================================

/**
 * Get the web funnel URL for a given alias
 *
 * @param alias - Web placement alias
 * @returns Full URL based on current environment
 */
export const getWebFunnelUrl = (alias: string): string => {
  const normalizedAlias = alias.replace(/^\/+/, '');
  return `${getPublicEdgeGatewayBaseUrl()}/${normalizedAlias}`;
};

/**
 * Get the web funnel preview URL (for before deployment)
 *
 * @param alias - Web placement alias
 * @returns Preview URL text
 */
export const getWebFunnelPreviewPath = (alias: string): string => {
  return `/${alias}`;
};

/**
 * Get funnel URL - uses custom domain if available, otherwise standard URL
 *
 * URL format (unified - no /w/ prefix):
 * - Standard:      https://dev-api.segmently.ai/summer-sale
 * - Custom domain: https://funnel.foodmate.online/summer-sale
 *
 * @param alias - Placement alias (e.g., "summer-sale")
 * @param customDomain - Active custom domain (e.g., "funnel.foodmate.online") or null
 * @returns Full funnel URL
 */
export const getFunnelUrl = (alias: string, customDomain?: string | null): string => {
  const normalizedAlias = alias.replace(/^\/+/, '');

  if (customDomain) {
    // Custom domain
    return `https://${customDomain.replace(/\/+$/, '')}/${normalizedAlias}`;
  }

  // Standard URL - direct access (no /w/ prefix)
  return `${getPublicEdgeGatewayBaseUrl()}/${normalizedAlias}`;
};

/**
 * Get the public Edge Gateway base URL used by Cloud URL and SDK-config links.
 *
 * This intentionally ignores localhost values from VITE_EDGE_GATEWAY_URL, since
 * localhost belongs only to Local Dev links driven by VITE_LOCAL_EDGE_GATEWAY_URL.
 */
export const getPublicEdgeGatewayBaseUrl = (): string => {
  const runtimeHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolvePublicEdgeGatewayBaseUrl({
    configuredBaseUrl: import.meta.env.VITE_EDGE_GATEWAY_URL,
    runtimeHostname,
  });
};

export const getWebPlacementSdkConfigUrl = (projectId: string, alias: string): string => {
  const normalizedAlias = alias.replace(/^\/+/, '');
  return `${getPublicEdgeGatewayBaseUrl()}/assets/web-v2/${projectId}/${normalizedAlias}/config.json`;
};

/**
 * Get local development Edge Gateway URL used by funnel preview links.
 *
 * Reads the dedicated VITE_LOCAL_EDGE_GATEWAY_URL, intentionally decoupled from
 * the public VITE_EDGE_GATEWAY_URL (which standard deployWebLocal keeps pointed
 * at cloud dev-api for Cloud URL + SDK-config links). The worktree launcher sets
 * this to the offset edge port (e.g. localhost:8180); standard local dev leaves
 * it unset and falls back to the conventional local Edge Gateway port.
 */
export const getLocalDevFunnelBaseUrl = (): string => {
  const baseUrl = import.meta.env.VITE_LOCAL_EDGE_GATEWAY_URL || 'http://localhost:8080';
  return baseUrl.replace(/\/+$/, '');
};

/**
 * Get local development funnel URL with Debug Panel.
 *
 * Always resolves to the local Edge Gateway (localhost:8080 by default, or the
 * worktree-offset port via VITE_LOCAL_EDGE_GATEWAY_URL) so the link opens the
 * locally-served funnel whose web-shell assets live on localhost:8081.
 */
export const getLocalDevFunnelUrl = (alias: string): string => {
  const normalizedAlias = alias.replace(/^\/+/, '');
  return `${getLocalDevFunnelBaseUrl()}/${normalizedAlias}`;
};

// =============================================================================
// A/B TEST ROUTING CONFIG (GCS)
// =============================================================================

/**
 * A/B test routing config variant (stored in GCS)
 *
 * @description Minimal variant info needed for Edge Gateway routing
 */
export interface WebABRoutingVariant {
  /** Variant ID */
  id: string;
  /** Human-readable variant name (for analytics) */
  name: string;
  /** Traffic weight (0-100) */
  weight: number;
  /** Target alias to serve (resolved WebPlacement's alias) */
  targetAlias: string;
  /** Optional machine-friendly tags for runtime filtering */
  tags?: string[];
}

/**
 * A/B test routing configuration (stored in GCS)
 *
 * @description
 * Uploaded to GCS when A/B test is published: web-ab/{alias}.json
 * ~200 bytes, enables fast variant resolution without Firestore reads.
 *
 * Edge Gateway flow:
 * 1. Check if web-ab/{alias}.json exists
 * 2. If yes: resolve variant using this config → fetch web/{targetAlias}/...
 * 3. If no: treat as single placement → fetch web/{alias}/...
 */
export interface WebABRoutingConfig {
  /** Salt for deterministic hash */
  abSalt: string;
  /** A/B test Firestore document ID (for analytics) */
  abTestId: string;
  /** Human-readable A/B test name (for analytics) */
  abTestName: string;
  /** Current A/B iteration ID (for analytics attribution) */
  abTestIterationId?: string;
  /** Deterministic identity of the routing-critical allocation state */
  routingHash?: string;
  /** Whether this routing state is randomized or delivery-only */
  allocationMode?: WebABAllocationMode;
  /** Optional machine-friendly iteration tags for runtime filtering */
  abTestIterationTags?: string[];
  /** Variant routing table */
  variants: WebABRoutingVariant[];
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if WebPlacement is a single (funnel) placement
 */
export const isSinglePlacement = (placement: WebPlacement): boolean =>
  !placement.type || placement.type === 'single';

/**
 * Type guard to check if WebPlacement is an A/B test
 */
export const isABTestPlacement = (placement: WebPlacement): boolean =>
  placement.type === 'ab_test';
